import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Button,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Slide,
  TextField,
  Grid,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Paper
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import Client from '../Client';
import Config from '../ClientConfig';
import BackofficeReportsApi from './backofficeReportsApi';

let d = new Date();
let d1Month = format(new Date(d.getFullYear(), d.getMonth(), 1), 'yyyy-MM-dd');
let dzMonth = format(new Date(d.getFullYear(), d.getMonth() + 1, 0), 'yyyy-MM-dd');

function thousandsSeparator(x) {
  if (x === undefined || x === null || Number.isNaN(x)) return '-';
  return Math.floor(x).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

const objectToCsv = function (data) {
  if (!data || data.length === 0) return '';

  const csvRows = [];
  const headers = Object.keys(data[0]);
  csvRows.push(headers.join(','));

  for (const row of data) {
    const values = headers.map((header) => {
      let val = row[header];
      if (val === null || val === undefined) val = '';
      const s = String(val).replace(/"/g, '""');
      return `"${s}"`;
    });
    csvRows.push(values.join(','));
  }

  return csvRows.join('\n');
};

function formatMoney(value) {
  if (value === undefined || value === null || value === '') return '-';
  const n = Number(value);
  if (Number.isNaN(n)) return '-';
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function daysBetweenInclusive(dateFrom, dateTo) {
  const from = new Date(dateFrom);
  const to = new Date(dateTo);

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;

  const diffMs = to.setHours(0, 0, 0, 0) - from.setHours(0, 0, 0, 0);
  return Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
}

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const MONTH_OPTIONS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

class SalesEstimateModal extends React.Component {
  constructor(props) {
    super(props);

    const now = new Date();
    const monthStart = format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd');
    const monthEnd = format(new Date(now.getFullYear(), now.getMonth() + 1, 0), 'yyyy-MM-dd');

    this.state = {
      client: new Client('none'),
      estimateMode: 'month',
      estimateYear: now.getFullYear(),
      estimateMonth: now.getMonth() + 1,
      estimateDateFrom: monthStart,
      estimateDateTo: monthEnd,
      loading: false,
      error: '',
      estimateData: null,
      estimateOrder: 'asc',
      estimateOrderBy: null,
    };
  }

  filterEstimateByGroup = (rows) => {
    const { selectedLocationGroup, locations } = this.props;

    if (selectedLocationGroup === 'all') return rows;

    const byName = {};
    const byDesc = {};

    (locations || []).forEach((loc) => {
      if (loc.name) {
        byName[loc.name.toLowerCase()] = loc;
      }

      if (loc.description) {
        byDesc[loc.description.toLowerCase()] = loc;
      }
    });

    return rows.filter((r) => {
      const locKey = (r.location_name || '').toString().toLowerCase();
      const loc = byName[locKey] || byDesc[locKey];

      if (!loc) return false;

      const groups = Array.isArray(loc.locationGroup) ? loc.locationGroup : [];
      return groups.includes(selectedLocationGroup);
    });
  };

  calculateEstimate = () => {
    if (this.state.estimateMode === 'period') {
      if (!this.state.estimateDateFrom || !this.state.estimateDateTo) {
        this.setState({ error: 'Please select both start date and end date.' });
        return;
      }

      if (this.state.estimateDateFrom > this.state.estimateDateTo) {
        this.setState({ error: 'Start date cannot be after end date.' });
        return;
      }

      const periodDays = daysBetweenInclusive(
        this.state.estimateDateFrom,
        this.state.estimateDateTo
      );

      if (!periodDays || periodDays > 40) {
        this.setState({
          error: 'The selected period cannot be longer than 40 days.',
        });
        return;
      }
    }
    this.setState(
      {
        loading: true,
        error: '',
        estimateData: null,
      },
      () => {
        window.setTimeout(() => {
          try {
            let queryData = {
                include_skipped: 1,
                include_daily_details: 0,
              };

              if (this.state.estimateMode === 'month') {
                queryData = {
                  ...queryData,
                  year: this.state.estimateYear,
                  month: this.state.estimateMonth,
                };
              } else {
                queryData = {
                  ...queryData,
                  date_from: this.state.estimateDateFrom,
                  date_to: this.state.estimateDateTo,
                };
              }

            const data = this.state.client.getData(
              Config.API_URL_INTELLIGENCE_INSIGHTS + 'sales-value-estimate/',
              queryData
            );

            if (data && !data.detail) {
              this.setState({
                estimateData: data,
                loading: false,
                error: '',
              });
            } else {
              this.setState({
                estimateData: null,
                loading: false,
                error: data?.detail || 'Could not calculate estimate.',
              });
            }
          } catch (error) {
            this.setState({
              estimateData: null,
              loading: false,
              error: error?.message || 'Could not calculate estimate.',
            });
          }
        }, 50);
      }
    );
  };

  handleEstimateSort = (property) => {
    const isAscending =
      this.state.estimateOrderBy === property && this.state.estimateOrder === 'asc';
    this.setState({
      estimateOrder: isAscending ? 'desc' : 'asc',
      estimateOrderBy: property,
    });
  };

  onDownloadEstimate = () => {
    const estimateData = this.state.estimateData;

    if (!estimateData || !Array.isArray(estimateData.by_location)) return;

    const rows = this.filterEstimateByGroup(estimateData.by_location).map((row) => ({
      location_iref: row.location_iref,
      location_name: row.location_name,
      estimated_value: row.estimated_value,
      usable_days: row.usable_days,
      skipped_days: row.skipped_days,
      average_confidence: row.average_confidence,
      minimum_confidence: row.minimum_confidence,
      maximum_confidence: row.maximum_confidence,
    }));

    const link = document.createElement('a');
    const periodLabel =
      this.state.estimateMode === 'month'
        ? `${this.state.estimateYear}_${String(this.state.estimateMonth).padStart(2, '0')}`
        : `${this.state.estimateDateFrom}_${this.state.estimateDateTo}`;

    link.download = `sales_estimate_${periodLabel}.csv`;

    const csvHeader =
      this.state.estimateMode === 'month'
        ? `Sales Estimate By Location - ${this.state.estimateYear}-${String(this.state.estimateMonth).padStart(2, '0')}`
        : `Sales Estimate By Location - ${this.state.estimateDateFrom} to ${this.state.estimateDateTo}`;
    const csv = '\uFEFF' + csvHeader + '\n' + objectToCsv(rows);

    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.click();
  };

  renderEstimateList() {
    const { loading, error, estimateData } = this.state;

    if (error) {
      return (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      );
    }

    if (!estimateData) {
      return (
        <Box
          sx={{
            py: 4,
            textAlign: 'center',
            color: '#64748b',
            fontWeight: 600,
          }}
        >
          Select month or custom period, then press Calculate.
        </Box>
      );
    }

    const rows = this.filterEstimateByGroup(estimateData.by_location || []);
    const { estimateOrder, estimateOrderBy } = this.state;
    const sortedRows = estimateOrderBy
      ? [...rows].sort((a, b) => {
          const aValue = estimateOrderBy === 'location_name'
            ? String(a.location_name || '').toLocaleLowerCase()
            : Number(a[estimateOrderBy] || 0);
          const bValue = estimateOrderBy === 'location_name'
            ? String(b.location_name || '').toLocaleLowerCase()
            : Number(b[estimateOrderBy] || 0);
          if (aValue < bValue) return estimateOrder === 'asc' ? -1 : 1;
          if (aValue > bValue) return estimateOrder === 'asc' ? 1 : -1;
          return 0;
        })
      : rows;

    const estimateSortLabel = (property, label) => (
      <TableSortLabel
        active={estimateOrderBy === property}
        direction={estimateOrderBy === property ? estimateOrder : 'asc'}
        onClick={() => this.handleEstimateSort(property)}
      >
        {label}
      </TableSortLabel>
    );

    return (
      <>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
            },
            gap: 1,
            mb: 1,
          }}
        >
          <Box
            sx={{
              p: 1.25,
              bgcolor: '#fff',
              borderRadius: '14px',
              border: '1px solid rgba(15, 23, 42, 0.06)',
            }}
          >
            <Typography sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.76rem' }}>
              Estimated value
            </Typography>

            <Typography
              sx={{
                mt: 0.35,
                color: '#111827',
                fontWeight: 900,
                fontSize: '1.25rem',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatMoney(estimateData.estimated_value)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 1.25,
              bgcolor: '#fff',
              borderRadius: '14px',
              border: '1px solid rgba(15, 23, 42, 0.06)',
            }}
          >
            <Typography sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.76rem' }}>
              Completeness
            </Typography>

            <Typography
              sx={{
                mt: 0.35,
                color: '#111827',
                fontWeight: 900,
                fontSize: '1.25rem',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {Number(estimateData.completeness_percent || 0).toFixed(1)}%
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
          <Chip
            size="small"
            label={`Locations: ${estimateData.active_locations_count || 0}`}
            sx={{ bgcolor: '#fff', fontWeight: 700 }}
          />

          <Chip
            size="small"
            label={`Usable days: ${estimateData.usable_location_days || 0}`}
            sx={{ bgcolor: '#fff', fontWeight: 700 }}
          />

          <Chip
            size="small"
            label={`Skipped: ${estimateData.skipped_location_days || 0}`}
            sx={{ bgcolor: '#fff', fontWeight: 700 }}
          />

          <Chip
            size="small"
            label={`Avg confidence: ${estimateData.confidence_summary?.average_confidence || '0.00'}%`}
            sx={{ bgcolor: '#fff', fontWeight: 700 }}
          />
        </Box>

        <TableContainer
          sx={{
            maxHeight: {
              xs: '52vh',
              sm: '56vh',
            },
            overflow: 'auto',
            bgcolor: '#fff',
            border: '1px solid rgba(15, 23, 42, 0.06)',
            borderRadius: '12px',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#f8fafc', color: '#1a237e', fontWeight: 800 }}>
                  {estimateSortLabel('location_name', 'Location')}
                </TableCell>

                <TableCell align="right" sx={{ bgcolor: '#f8fafc', color: '#1a237e', fontWeight: 800 }}>
                  {estimateSortLabel('estimated_value', 'Estimate')}
                </TableCell>

                <TableCell align="right" sx={{ bgcolor: '#f8fafc', color: '#1a237e', fontWeight: 800 }}>
                  {estimateSortLabel('average_confidence', 'Conf.')}
                </TableCell>

                <TableCell align="right" sx={{ bgcolor: '#f8fafc', color: '#1a237e', fontWeight: 800 }}>
                  {estimateSortLabel('usable_days', 'Days')}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedRows.map((row) => (
                <TableRow key={row.location_iref} hover>
                  <TableCell
                    sx={{
                      fontWeight: 650,
                      maxWidth: 220,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.location_name}
                  </TableCell>

                  <TableCell align="right" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(row.estimated_value)}
                  </TableCell>

                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {row.average_confidence || '0.00'}%
                  </TableCell>

                  <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {row.usable_days}/{Number(row.usable_days || 0) + Number(row.skipped_days || 0)}
                  </TableCell>
                </TableRow>
              ))}

              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box sx={{ py: 4, color: '#64748b', fontWeight: 600 }}>
                      No estimate data
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </>
    );
  }

  render() {
    const { open, onClose } = this.props;
    const {
      estimateMode,
      estimateYear,
      estimateMonth,
      estimateDateFrom,
      estimateDateTo,
      loading,
      estimateData,
    } = this.state;

    const currentYear = new Date().getFullYear();
    const yearOptions = [];

    for (let y = currentYear - 2; y <= currentYear + 2; y += 1) {
      yearOptions.push(y);
    }

    return (
      <Dialog
        open={open}
        onClose={onClose}
        TransitionComponent={Transition}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: {
              xs: 'calc(100% - 16px)',
              sm: '92vw',
              md: '78vw',
              lg: '68vw',
            },
            height: {
              xs: '92vh',
              sm: '90vh',
            },
            maxWidth: 'none',
            borderRadius: {
              xs: '14px',
              sm: '16px',
            },
            overflow: 'hidden',
          },
        }}
      >
        <AppBar sx={{ position: 'sticky' }} color="default" elevation={1}>
          <Toolbar variant="dense">
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close" size="small">
              <CloseIcon fontSize="small" />
            </IconButton>

            <Typography sx={{ ml: 2, flex: 1 }} variant="subtitle1" component="div">
              Sales Target Estimate
            </Typography>

            <Button
              size="small"
              startIcon={<FileDownloadIcon />}
              onClick={this.onDownloadEstimate}
              disabled={!estimateData || loading}
              sx={{ mr: 1 }}
            >
              Download
            </Button>
          </Toolbar>
        </AppBar>

        <Box
          sx={{
            position: 'relative',
            p: 1.5,
            bgcolor: '#f8fafc',
            height: '100%',
            overflow: 'auto',
          }}
        >
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                label="Period type"
                select
                fullWidth
                size="small"
                value={estimateMode}
                onChange={(e) => {
                  this.setState({
                    estimateMode: e.target.value,
                    estimateData: null,
                    error: '',
                  });
                }}
                disabled={loading}
                sx={{ bgcolor: '#fff' }}
              >
                <MenuItem value="month">Month</MenuItem>
                <MenuItem value="period">Custom period</MenuItem>
              </TextField>
            </Grid>

            {estimateMode === 'month' ? (
              <>
                <Grid item xs={12} sm={4} md={3}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={estimateMonth}
                      onChange={(e) =>
                        this.setState({
                          estimateMonth: Number(e.target.value),
                          estimateData: null,
                          error: '',
                        })
                      }
                      displayEmpty
                      disabled={loading}
                      sx={{ bgcolor: '#fff' }}
                    >
                      {MONTH_OPTIONS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {m.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    label="Year"
                    select
                    fullWidth
                    size="small"
                    value={estimateYear}
                    onChange={(e) =>
                      this.setState({
                        estimateYear: Number(e.target.value),
                        estimateData: null,
                        error: '',
                      })
                    }
                    disabled={loading}
                    sx={{ bgcolor: '#fff' }}
                  >
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    label="From"
                    type="date"
                    fullWidth
                    size="small"
                    value={estimateDateFrom}
                    onChange={(e) =>
                      this.setState({
                        estimateDateFrom: e.target.value,
                        estimateData: null,
                        error: '',
                      })
                    }
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    sx={{ bgcolor: '#fff' }}
                  />
                </Grid>

                <Grid item xs={12} sm={4} md={3}>
                  <TextField
                    label="To"
                    type="date"
                    fullWidth
                    size="small"
                    value={estimateDateTo}
                    onChange={(e) =>
                      this.setState({
                        estimateDateTo: e.target.value,
                        estimateData: null,
                        error: '',
                      })
                    }
                    disabled={loading}
                    InputLabelProps={{ shrink: true }}
                    sx={{ bgcolor: '#fff' }}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={4} md={3}>
              <Button
                size="small"
                variant="contained"
                fullWidth
                onClick={this.calculateEstimate}
                disabled={
                  loading ||
                  (estimateMode === 'period' && (!estimateDateFrom || !estimateDateTo))
                }
                startIcon={<QueryStatsIcon />}
                sx={{
                  height: 40,
                  borderRadius: '12px',
                }}
              >
                Calculate
              </Button>
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.25 }} />

          {this.renderEstimateList()}

          {this.state.loading && (
            <Box
              role="status"
              aria-live="polite"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(15, 23, 42, 0.16)',
                backgroundImage:
                  'radial-gradient(ellipse at center, rgba(15, 23, 42, 0.08) 0%, rgba(15, 23, 42, 0.18) 75%, rgba(15, 23, 42, 0.22) 100%)',
                zIndex: 20,
                pointerEvents: 'auto',
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  px: 2.5,
                  py: 1.5,
                  borderRadius: '14px',
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  border: '1px solid rgba(15, 23, 42, 0.08)',
                }}
              >
                <CircularProgress size={22} color="inherit" />
                <Typography variant="body2">Calculating estimate…</Typography>
              </Paper>
            </Box>
          )}
        </Box>
      </Dialog>
    );
  }
}

class SalesForecastLocationList extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      client: new Client('none'),
      saleslocation_list: [],
      page: 0,
      rowsPerPage: -1,
      order: 'asc',
      orderBy: null,
      dateFrom: d1Month,
      dateTo: dzMonth,
      sx_hidden: { display: 'none' },
      value: '1',
      filterData: {},
      targetsOpen: false,
      locationGroups: [],
      locations: [],
      selectedLocationGroup: 'all',
      estimateDialogOpen: false,
    };

    this.getData = this.getData.bind(this);
    this.doSync = this.doSync.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangeRowsPerPage = this.handleChangeRowsPerPage.bind(this);
    this.addDateFrom = this.addDateFrom.bind(this);
    this.addDateTo = this.addDateTo.bind(this);
    this.selectPeriod = this.selectPeriod.bind(this);
  }

  componentDidMount() {
    this.getData();
    this.loadLocationGroups();
    this.loadLocations();
  }

  getData() {
    let queryData = {
      dateFrom: this.state.dateFrom,
      dateTo: this.state.dateTo,
    };

    let data = BackofficeReportsApi.getSalesForecast(this.state.client, queryData);

    if (data) {
      this.setState({ saleslocation_list: data });
    }

    this.setState({ filterData: queryData });
  }

  doSync() {
    this.getData();
  }

  loadLocationGroups = () => {
    const data = BackofficeReportsApi.getLocationGroups(this.state.client);

    if (data && Array.isArray(data.locationsGroups)) {
      this.setState({ locationGroups: data.locationsGroups });
    } else {
      this.setState({ locationGroups: [] });
    }
  };

  loadLocations = () => {
    const data = this.state.client.getData(
      Config.API_URL_LOCATIONS + 'list_locations/',
      { status: 1 }
    );

    if (data && Array.isArray(data.locations)) {
      this.setState({ locations: data.locations });
    } else if (Array.isArray(data)) {
      this.setState({ locations: data });
    }
  };

  filterByGroup = (rows) => {
    const { selectedLocationGroup, locations } = this.state;

    if (selectedLocationGroup === 'all') return rows;

    const byName = {};
    const byDesc = {};

    locations.forEach((loc) => {
      if (loc.name) {
        byName[loc.name.toLowerCase()] = loc;
      }

      if (loc.description) {
        byDesc[loc.description.toLowerCase()] = loc;
      }
    });

    return rows.filter((r) => {
      const locKey = (r.location || '').toString().toLowerCase();
      let loc = byName[locKey];

      if (!loc) return false;

      const groups = Array.isArray(loc.locationGroup) ? loc.locationGroup : [];
      return groups.includes(selectedLocationGroup);
    });
  };

  computeSubtotals = (rows) => {
    let salesMTD = 0;
    let forecastTM = 0;
    let salesLM = 0;

    rows.forEach((r) => {
      salesMTD += parseFloat(r.salesMTD || 0);
      forecastTM += parseFloat(r.forecastTM || 0);
      salesLM += parseFloat(r.salesLM || 0);
    });

    const pctLM = salesLM > 0 ? (salesMTD / salesLM) * 100 : 0;

    return { salesMTD, forecastTM, salesLM, pctLM };
  };

  addDateFrom = (event) => {
    this.setState({ dateFrom: event.target.value });
  };

  addDateTo = (event) => {
    this.setState({ dateTo: event.target.value });
  };

  selectPeriod = (event) => {
    switch (event.target.value) {
      case 'month':
        this.setState({ sx_hidden: { display: 'none' } }, this.setState({ dateFrom: d1Month }));
        this.setState({ dateTo: dzMonth }, () => this.doSync());
        break;

      default:
        this.setState({ sx_hidden: {} }, () => this.doSync());
    }
  };

  handleChangePage = (event, newPage) => {
    this.setState({ page: newPage });
  };

  handleChangeRowsPerPage = (event) => {
    this.setState({ rowsPerPage: +event.target.value });
    this.setState({ page: 0 });
  };

  handleRequestSort = (property) => {
    const isAscending = this.state.orderBy === property && this.state.order === 'asc';
    this.setState({ order: isAscending ? 'desc' : 'asc', orderBy: property, page: 0 });
  };

  onDownload = () => {
    const link = document.createElement('a');
    link.download = 'salesForecast.csv';

    let csvData = this.state.saleslocation_list?.result
      ? objectToCsv(this.state.saleslocation_list.result)
      : '';

    let csvHeader = 'Sales Forecast';
    let csv = csvHeader + '\n' + csvData;

    link.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    link.click();
  };

  render() {
    let page = this.state.page;
    let rowsPerPage = this.state.rowsPerPage;
    let locationSalesList = [];

    if (this.state.saleslocation_list) {
      if (this.state.saleslocation_list.result) {
        let counter = 0;

        Object.keys(this.state.saleslocation_list.result).forEach((key) => {
          let fData = this.state.saleslocation_list.result[key];

          let p = {
            id: counter + 1,
            location: fData.location,
            forecastTM: fData.forecastTM,
            salesMTD: fData.salesMTD,
            forecastMTD: fData.forecastMTD,
            salesLM: fData.salesLM,
          };

          counter += 1;
          locationSalesList.push(p);
        });
      }
    }

    const usedGroupIds = new Set();

    this.state.locations.forEach((loc) => {
      const groups = Array.isArray(loc.locationGroup) ? loc.locationGroup : [];
      groups.forEach((g) => usedGroupIds.add(g));
    });

    const filteredList = this.filterByGroup(locationSalesList);
    const subtotals = this.computeSubtotals(filteredList);
    const { order, orderBy } = this.state;
    const sortedFilteredList = orderBy
      ? [...filteredList].sort((a, b) => {
          const getValue = (row) => {
            if (orderBy === 'location') {
              return String(row.location || '').toLocaleLowerCase();
            }
            if (orderBy === 'pctLM') {
              const salesLM = Number(row.salesLM || 0);
              return salesLM === 0 ? 0 : (Number(row.forecastTM || 0) / salesLM) * 100;
            }
            return Number(row[orderBy] || 0);
          };
          const aValue = getValue(a);
          const bValue = getValue(b);
          if (aValue < bValue) return order === 'asc' ? -1 : 1;
          if (aValue > bValue) return order === 'asc' ? 1 : -1;
          return 0;
        })
      : filteredList;

    const sortLabel = (property, label) => (
      <TableSortLabel
        active={orderBy === property}
        direction={orderBy === property ? order : 'asc'}
        onClick={() => this.handleRequestSort(property)}
      >
        {label}
      </TableSortLabel>
    );

    const tableContainerSx = {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      width: '100%',
      maxWidth: '100%',
      overflowX: {
        xs: 'auto',
        sm: 'auto',
        md: 'hidden',
      },
      overflowY: 'auto',
      borderTop: '1px solid rgba(15, 23, 42, 0.06)',
      '&::-webkit-scrollbar': {
        height: 8,
        width: 8,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(15, 23, 42, 0.18)',
        borderRadius: 999,
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'rgba(15, 23, 42, 0.04)',
      },
    };

    const tableSx = {
      minWidth: {
        xs: 500,
        sm: 600,
        md: '100%',
      },
      width: '100%',
      maxWidth: '100%',
      tableLayout: {
        xs: 'fixed',
        md: 'auto'
      },
      '& .MuiTableCell-root': {
        borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
        fontSize: {
          xs: '0.76rem',
          sm: '0.84rem',
          md: '0.88rem',
          lg: '0.9rem',
        },
      },
      '& .MuiTableRow-hover:hover': {
        backgroundColor: 'rgba(26, 35, 126, 0.035)',
      },
    };

    const headerCellSx = {
      py: {
        xs: 0.5,
        sm: 0.7,
        md: 0.85,
      },
      px: {
        xs: 0.35,
        sm: 0.65,
        md: 1,
      },
      bgcolor: '#f8fafc',
      color: '#1a237e',
      fontWeight: 650,
      lineHeight: 1.15,
      whiteSpace: {
        xs: 'normal',
        md: 'normal',
      },
      overflow: 'visible',
      textOverflow: 'clip',
      wordBreak: 'normal',
    };

    const bodyCellSx = {
      py: {
        xs: 0.55,
        sm: 0.75,
        md: 0.8,
      },
      px: {
        xs: 0.35,
        sm: 0.65,
        md: 1,
      },
      whiteSpace: 'nowrap',
      color: '#111827',
      fontWeight: 450,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontVariantNumeric: 'tabular-nums',
    };

    const nameHeaderCellSx = {
      ...headerCellSx,
      position: 'sticky',
      left: 0,
      zIndex: 4,
    };

    const nameCellSx = {
      ...bodyCellSx,
      position: 'sticky',
      left: 0,
      zIndex: 2,
      bgcolor: '#fff',
      fontWeight: 550,
      fontVariantNumeric: 'normal',
    };

    const subtotalRowSx = {
      '& .MuiTableCell-root': {
        bgcolor: 'rgba(26, 35, 126, 0.055)',
        color: '#111827',
        fontWeight: 900,
        borderTop: '1px solid rgba(26, 35, 126, 0.12)',
      },
      '& .MuiTableCell-root:first-of-type': {
        color: '#1a237e',
      },
    };

    const paginationSx = {
      flexShrink: 0,
      borderTop: '1px solid rgba(15, 23, 42, 0.06)',
      bgcolor: '#fff',
      minHeight: {
        xs: 44,
        sm: 52,
      },
      '& .MuiTablePagination-toolbar': {
        minHeight: {
          xs: 44,
          sm: 52,
        },
        px: {
          xs: 0.5,
          sm: 1,
        },
        flexWrap: {
          xs: 'wrap',
          sm: 'nowrap',
        },
        gap: {
          xs: 0.25,
          sm: 0.5,
        },
      },
      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
        fontSize: {
          xs: '0.88rem',
          sm: '0.92rem',
          md: '1rem',
        },
        m: 0,
      },
      '& .MuiTablePagination-actions': {
        ml: {
          xs: 0,
          sm: 1,
        },
      },
    };

    return (
      <CardContent
        sx={{
          position: 'relative',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0,
          boxSizing: 'border-box',
          height: {
            xs: 560,
            sm: 600,
            md: '100%',
            lg: '100%',
          },
          maxHeight: {
            xs: 560,
            sm: 600,
            md: '100%',
            lg: '100%',
          },
          minHeight: 0,
          p: '0 !important',
          borderRadius: {
            xs: '14px',
            sm: '16px',
          },
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
          border: '1px solid rgba(15, 23, 42, 0.06)',
          bgcolor: '#fff',
        }}
      >
        <Box
          sx={{
            px: {
              xs: 0.75,
              sm: 1,
            },
            py: {
              xs: 0.65,
              sm: 0.85,
            },
            flexShrink: 0,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            bgcolor: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: {
                xs: 'stretch',
                sm: 'center',
              },
              justifyContent: 'space-between',
              gap: {
                xs: 0.75,
                sm: 1,
              },
              flexDirection: {
                xs: 'column',
                sm: 'row',
              },
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: '#1a237e',
                  fontWeight: 800,
                  fontSize: {
                    xs: '0.9rem',
                    sm: '1rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Forecast TM
              </Typography>

              <Typography
                sx={{
                  mt: 0.15,
                  color: '#64748b',
                  fontWeight: 600,
                  fontSize: {
                    xs: '0.72rem',
                    sm: '0.78rem',
                  },
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                Sales forecast by location
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: {
                  xs: 'space-between',
                  sm: 'flex-end',
                },
                gap: 0.75,
                flexWrap: 'wrap',
              }}
            >
              <FormControl
                size="small"
                sx={{
                  minWidth: {
                    xs: 180,
                    sm: 200,
                  },
                  maxWidth: {
                    xs: 'calc(100% - 44px)',
                    sm: 240,
                  },
                  '& .MuiInputBase-root': {
                    height: 36,
                    borderRadius: '12px',
                    fontSize: {
                      xs: '0.82rem',
                      sm: '0.88rem',
                    },
                    bgcolor: '#fff',
                  },
                }}
              >
                <Select
                  labelId="lg-label"
                  value={this.state.selectedLocationGroup}
                  onChange={(e) => this.setState({ selectedLocationGroup: e.target.value, page: 0 })}
                  displayEmpty
                  sx={{
                    '& .MuiSelect-select': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                >
                  <MenuItem value="all">All groups</MenuItem>

                  {this.state.locationGroups.map((lg) => (
                    <MenuItem key={lg.iref} value={lg.iref}>
                      {lg.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Tooltip title="Sales target estimate">
                <Button
                  size="small"
                  onClick={() => this.setState({ estimateDialogOpen: true })}
                  variant="contained"
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    p: 0,
                    flexShrink: 0,
                    borderRadius: '12px',
                    color: '#1a237e',
                    bgcolor: 'rgba(26, 35, 126, 0.08)',
                    boxShadow: 'none',
                    border: '1px solid rgba(26, 35, 126, 0.12)',
                    '&:hover': {
                      bgcolor: 'rgba(26, 35, 126, 0.12)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  <QueryStatsIcon fontSize="small" />
                </Button>
              </Tooltip>

              <Tooltip title="Download CSV">
                <Button
                  size="small"
                  onClick={this.onDownload}
                  variant="contained"
                  sx={{
                    minWidth: 36,
                    width: 36,
                    height: 36,
                    p: 0,
                    flexShrink: 0,
                    borderRadius: '12px',
                    color: '#1a237e',
                    bgcolor: 'rgba(26, 35, 126, 0.08)',
                    boxShadow: 'none',
                    border: '1px solid rgba(26, 35, 126, 0.12)',
                    '&:hover': {
                      bgcolor: 'rgba(26, 35, 126, 0.12)',
                      boxShadow: 'none',
                    },
                  }}
                >
                  <FileDownloadIcon fontSize="small" />
                </Button>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <TableContainer sx={tableContainerSx}>
          <Table stickyHeader size="small" aria-label="forecast table" sx={tableSx}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    ...nameHeaderCellSx,
                    width: {
                      xs: 120,
                      sm: 170,
                      md: '30%',
                    },
                  }}
                >
                  {sortLabel('location', 'Location')}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    ...headerCellSx,
                    width: {
                      xs: 92,
                      sm: 105,
                      md: '18%',
                    },
                  }}
                >
                  {sortLabel('salesMTD', 'Sales MTD')}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    ...headerCellSx,
                    width: {
                      xs: 96,
                      sm: 115,
                      md: '20%',
                    },
                  }}
                >
                  {sortLabel('forecastTM', 'Forecast')}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    ...headerCellSx,
                    width: {
                      xs: 92,
                      sm: 105,
                      md: '18%',
                    },
                  }}
                >
                  {sortLabel('salesLM', 'Sales LM')}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    ...headerCellSx,
                    width: {
                      xs: 68,
                      sm: 80,
                      md: '14%',
                    },
                  }}
                >
                  {sortLabel('pctLM', '% LM')}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(rowsPerPage > 0
                ? sortedFilteredList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : sortedFilteredList
              ).map((location) => {
                return (
                  <TableRow hover key={location.id} tabIndex={-1}>
                    <TableCell
                      sx={{
                        ...nameCellSx,
                        width: {
                          xs: 120,
                          sm: 170,
                          md: '30%',
                        },
                      }}
                    >
                      {location.location}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {thousandsSeparator(parseInt(location.salesMTD))}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {thousandsSeparator(parseInt(location.forecastTM))}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {thousandsSeparator(parseInt(location.salesLM))}
                    </TableCell>

                    <TableCell align="right" sx={bodyCellSx}>
                      {location.salesLM === 0
                        ? '0%'
                        : parseInt((location.forecastTM / location.salesLM) * 100) + '%'}
                    </TableCell>
                  </TableRow>
                );
              })}

              {this.state.selectedLocationGroup !== "all" && (
                <TableRow sx={subtotalRowSx}>
                  <TableCell
                    sx={{
                      ...nameCellSx,
                      bgcolor: 'rgba(26, 35, 126, 0.055)',
                    }}
                  >
                    Subtotal
                  </TableCell>

                  <TableCell align="right" sx={bodyCellSx}>
                    {thousandsSeparator(parseInt(subtotals.salesMTD))}
                  </TableCell>

                  <TableCell align="right" sx={bodyCellSx}>
                    {thousandsSeparator(parseInt(subtotals.forecastTM))}
                  </TableCell>

                  <TableCell align="right" sx={bodyCellSx}>
                    {thousandsSeparator(parseInt(subtotals.salesLM))}
                  </TableCell>

                  <TableCell align="right" sx={bodyCellSx}>
                    {subtotals.pctLM.toFixed(1)}%
                  </TableCell>
                </TableRow>
              )}

              {filteredList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Box
                      sx={{
                        py: 4,
                        color: 'text.secondary',
                        fontSize: {
                          xs: '0.86rem',
                          sm: '0.92rem',
                        },
                      }}
                    >
                      No data
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, { value: -1, label: 'All' }]}
          labelRowsPerPage="Rows:"
          component="div"
          count={filteredList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={this.handleChangePage}
          onRowsPerPageChange={this.handleChangeRowsPerPage}
          sx={paginationSx}
        />

        {this.state.estimateDialogOpen && (
          <SalesEstimateModal
            open={this.state.estimateDialogOpen}
            onClose={() => this.setState({ estimateDialogOpen: false })}
            selectedLocationGroup={this.state.selectedLocationGroup}
            locations={this.state.locations}
          />
        )}
      </CardContent>
    );
  }
}

export default SalesForecastLocationList;
